import { injectedPagebreakMarkerId } from '../PagebreakTracker.js';

const DEFAULT_RESOURCE_TYPE = 'application/xhtml+xml';

/**
 * @typedef {Object} PrintedPageLocator
 * @property {{ href: string, type: string, locations: Object }} locator
 * @property {'page-list' | 'injected' | 'estimated'} source
 */

/**
 * Maps every printed page of a book to locator data the reader can turn to.
 *
 * Locators come back as plain RWPM JSON, the shape an add-on anchor or a saved
 * position already carries, so `goToLocation` turns them into navigator
 * Locators the same way it turns those.
 *
 * Three sources feed the map, the most reliable winning per page:
 *
 * - `page-list`: the publication's `pageList` collection, which go-toolkit emits
 *   from the book's `<nav epub:type="page-list">`. Exact, but only publisher-made
 *   books carry one. The platform injector writes markers, never a nav.
 * - `injected`: the spine's `page_from..page_to` ranges. The locator anchors to
 *   the platform marker id and carries the page's midpoint progression.
 * - `estimated`: spines written before the page fields existed carry only word
 *   counts, so pages are spread over chapters in proportion to their words.
 *
 * @param {Object} params
 * @param {import('@readium/shared').Publication | null} [params.publication]
 * @param {Array<Object>} [params.spine] - Host spine entries (`href`, `words`, `previous_words`, `page_from`, `page_to`)
 * @param {number} [params.totalPages] - Highest printed page label of the book
 * @returns {Map<number, PrintedPageLocator>}
 */
export function buildPrintedPageLocators({ publication = null, spine = [], totalPages = 0 } = {}) {
    // linkWithHref walks reading order, resources and links on every call, so each chapter is looked up once.
    const resourceTypes = new Map();
    const resourceType = href => {
        if (!resourceTypes.has(href)) {
            resourceTypes.set(href, publication?.linkWithHref(href)?.type ?? DEFAULT_RESOURCE_TYPE);
        }
        return resourceTypes.get(href);
    };

    const ranged = spine.filter(hasPageRange);
    const [source, pages] = ranged.length
        ? ['injected', rangedPages(ranged)]
        : ['estimated', estimatedPages(spine, totalPages)];

    const locators = new Map();
    for (const [page, href, locations] of pages) {
        locators.set(page, { source, locator: { href, type: resourceType(href), locations } });
    }

    for (const link of publication?.linksWithRole('pageList')?.items ?? []) {
        const page = Number(link.title);
        if (!Number.isInteger(page) || page <= 0) continue;

        const locator = publication.manifest.locatorFromLink(link);
        if (locator) locators.set(page, { source: 'page-list', locator: locator.serialize() });
    }

    return locators;
}

function hasPageRange(entry) {
    return Number.isInteger(entry.page_from) && Number.isInteger(entry.page_to) && entry.page_to >= entry.page_from;
}

/** Interpolates over the label range rather than the marker count, so skipped labels keep their place. */
function* rangedPages(entries) {
    for (const entry of entries) {
        const span = entry.page_to - entry.page_from + 1;
        for (let page = entry.page_from; page <= entry.page_to; page++) {
            // The navigator resolves the html id first and falls back to the
            // progression when the id is missing, which is what a
            // publisher-marked book does: its ids are its own, unknown here.
            yield [
                page,
                entry.href,
                { fragments: [injectedPagebreakMarkerId(page)], progression: (page - entry.page_from + 0.5) / span },
            ];
        }
    }
}

/** Spreads pages 1..totalPages over the book's word axis and reads each page's midpoint off the chapter it falls in. */
function chapterHoldsWord(entry, wordIndex) {
    return entry.words > 0 && wordIndex >= entry.previous_words && wordIndex < entry.previous_words + entry.words;
}

function* estimatedPages(spine, totalPages) {
    const totalWords = spine.reduce((sum, entry) => sum + (entry.words ?? 0), 0);
    if (!(totalPages > 0) || !(totalWords > 0)) return;

    const wordsPerPage = totalWords / totalPages;
    for (let page = 1; page <= totalPages; page++) {
        const midpoint = (page - 0.5) * wordsPerPage;
        const entry = spine.find(candidate => chapterHoldsWord(candidate, midpoint));
        if (!entry) continue;

        yield [page, entry.href, { progression: (midpoint - entry.previous_words) / entry.words }];
    }
}
